import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import BrowseListContainer from '../../product-listing-page/components/BrowseListContainer';
import SearchPageHeader from './SearchPageHeader';
import { getBrowseApiHeaders, ITEMS_PER_PAGE } from '../SearchPageUtils/constants';
import '../styles.scss';
import '../../../../styles/components/pagination.scss';
import '../../../../styles/templates/search.scss';
import '../../product-listing-page/style.scss';
import { productServiceClient } from '../../_shared/apolloClientUtils';
import { loader } from 'graphql.macro';
import AloSidebar from '../../_shared/ui-components/YogaUI/alo-ui/AloSidebar';

import {
  BloomreachPageTypes,
  fireGA4viewPLPproductsEvent,
  pushBloomreachPageViewEventToDataLayer,
} from '../../../utils/gtm-data-layer';
import {
  BrFilter,
  GetSearchProductsQueryVariables,
  MerchSource,
  SearchProductsAProductConnectionFragment,
} from 'src/generated/product-service-graphql';
import { searchRespProductServiceProductToProductNode } from '../../../sections/header/Search/utils';
import { PlpContext } from '../../product-listing-page/PLPContext';
import BloomreachFilters from '../../product-listing-page/components/BloomreachFilters';
import AppliedFilters from './AppliedFilters';
import { MUI_MOBILE_BREAKPOINT } from '../../../utils/constants';
import PendingFilters from './PendingFilters';
import NoResultsPage from './NoResultsPage';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useSearchParamHelpers } from '../../_shared/react-hooks';
import { getFiltersCount } from '../../product-listing-page/PlpUtils/getFiltersCount';
import ButtonWithProgress from '../../_shared/ui-components/YogaUI/ButtonWithProgress';
import { prependLocaleToPath } from '../../../utils/localization';
import { useBrFilters } from '../../product-listing-page/components/useBrFilters';
import { SORT_BY_KEY } from '../../product-listing-page/components/sorting-utils';
import { validateSortBy } from '../../product-listing-page/PlpUtils/plpHelper';
import { pushPageViewEventToFS } from 'src/scripts/utils/full-story';

const GetSearchProductsGQL = loader('./GetSearchProducts.product-service.graphql');

const SearchPageContainer = () => {
  const [searchData, setSearchData] = useState<SearchProductsAProductConnectionFragment | null>(null);
  const [brFilters, setBrFilters] = useState<BrFilter[]>([]);
  const offsetRef = useRef<number>(0);

  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });
  const { searchParams } = useSearchParamHelpers();
  const { isPlpFiltersDrawerOpen, togglePlpFiltersDrawer } = useContext(PlpContext);

  const query = searchParams.get('q');
  const originalQuery = searchParams.get('origq') || query;
  const selectedSortKey = validateSortBy(searchParams.get(SORT_BY_KEY));

  const {
    handlePendingFilterChange,
    handleClearAllFilters,
    closeFiltersSidebar,
    appliedFilters,
    findFilterLabelByValue,
    availableFilters,
  } = useBrFilters({
    brFilters,
  });

  useEffect(() => {
    setBrFilters(searchData?.filters || []);
  }, [searchData]);

  useEffect(() => {
    pushBloomreachPageViewEventToDataLayer(BloomreachPageTypes.SearchResult, {
      search_term: originalQuery,
    });
    //!![REVIST] For FS POC
    pushPageViewEventToFS({ search_term: originalQuery });
  }, [originalQuery]);

  useEffect(() => {
    if (!searchData) return;
    const startIndex = offsetRef.current;
    const productNodes = searchData?.nodes || [];
    const newlyFetchedProducts = productNodes
      .slice(startIndex, productNodes.length)
      .map((p) => searchRespProductServiceProductToProductNode(p));
    fireGA4viewPLPproductsEvent(newlyFetchedProducts, 'search-page', startIndex, 'search-page', MerchSource.Bloomreach);
  }, [searchData]);

  // Data source for the results displayed on the main page
  const queryVariables: GetSearchProductsQueryVariables = useMemo(() => {
    offsetRef.current = 0;
    return {
      // Replace smart quotes (‘ and ’ and “ and ”) with regular quotes
      query: query?.replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"') || '',
      offset: 0,
      limit: ITEMS_PER_PAGE,
      sortKey: selectedSortKey,
      filters: appliedFilters,
    };
  }, [query, selectedSortKey, appliedFilters]);

  const { data, loading, fetchMore, networkStatus } = useQuery(GetSearchProductsGQL, {
    client: productServiceClient,
    context: { headers: getBrowseApiHeaders() },
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
    onError: (e) => {
      console.error(e);
    },
  });
  const searchProductsResult = data?.searchProducts;
  if (searchProductsResult?.__typename === 'Redirect') {
    window.location.href = prependLocaleToPath(searchProductsResult.path);
  } else if (searchProductsResult?.__typename === 'AProductConnection') {
    setSearchData(searchProductsResult);
    const totalCount = searchProductsResult.totalCount;
    document.title = `Search: ${totalCount} result${totalCount > 1 ? 's' : ''} found for "${query}" | ALO`;
  }

  const handleLoadMore = () => {
    const newOffset = offsetRef.current + ITEMS_PER_PAGE;
    if (newOffset >= searchProductsResult.totalCount) return;
    const fetchMoreVariables = {
      ...queryVariables,
      offset: newOffset,
    };
    fetchMore({ variables: fetchMoreVariables });
    offsetRef.current = newOffset;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [searchParams]);

  const totalProducts = searchData?.totalCount || 0;
  const nextOffset = offsetRef.current + ITEMS_PER_PAGE;
  return searchData ? (
    <div id={'REACT-PLP'} className="search-page" mobile-view={isMobile}>
      {/* only show this page if there are no results for the query and no params */}
      {searchData.totalCount <= 0 && appliedFilters.length < 1 ? (
        <NoResultsPage query={query || ''} />
      ) : (
        <>
          <SearchPageHeader
            query={query || ''}
            resultsQuantity={searchData?.totalCount}
            openPlpFiltersDrawer={() => togglePlpFiltersDrawer(true)}
            totalAppliedFilterCount={getFiltersCount(appliedFilters)}
            isMobile={isMobile}
            sortOptions={searchData.sorts}
            filters={searchData?.filters || []}
            selectedFilters={appliedFilters}
          />
          <AppliedFilters
            filters={appliedFilters}
            onClearAllFilters={handleClearAllFilters}
            findFilterLabelByValue={findFilterLabelByValue}
          />
          <div id="PLPContainer" className="container-fluid fw-grid__wrapper content-wrapper">
            <BrowseListContainer
              products={searchData.nodes.map((p) => searchRespProductServiceProductToProductNode(p))}
              onScrollToEnd={handleLoadMore}
              isLoading={loading && networkStatus !== NetworkStatus.ready}
              totalProducts={totalProducts}
              clearAllFilters={handleClearAllFilters}
              collectionHandle={'search-page'}
              collectionShopifyId={''}
              source="search-page"
              merchant={MerchSource.Bloomreach}
              hasNextPage={nextOffset < totalProducts}
            />
          </div>
          <AloSidebar
            mobileWidth={`100vw`}
            wrapperClass="filter-drawer-container"
            visible={isPlpFiltersDrawerOpen}
            onClose={closeFiltersSidebar}
            title={`Filters Selected (${getFiltersCount(appliedFilters)})`}
            headerBorder={true}
            anchor={'left'}
            desktopWidth={490}
            postHeader={
              <PendingFilters
                filters={appliedFilters}
                onRemove={handlePendingFilterChange}
                onRemoveAll={handleClearAllFilters}
                findFilterLabelByValue={findFilterLabelByValue}
              />
            }
          >
            <div className="filters-wrapper">
              <BloomreachFilters
                filters={brFilters}
                availableFilters={availableFilters}
                selectedFilters={appliedFilters}
                onChange={handlePendingFilterChange}
                showSortOptions={isMobile}
                sortOptions={searchData.sorts}
              />
              <div className="sticky-filters-button">
                <ButtonWithProgress
                  options={{
                    onClick: closeFiltersSidebar,
                    showProgressAnimation: loading,
                    buttonTypeClass: 'button-primary',
                    cta: `View Results${searchData.totalCount !== undefined ? ` (${searchData.totalCount})` : ''}`,
                  }}
                />
              </div>
            </div>
          </AloSidebar>
        </>
      )}
    </div>
  ) : null;
};

export default SearchPageContainer;
